import { aplClient } from "./apiClient";
import _ from "lodash";
import { PalletDetail } from "../types/palletDetail";
import { Guid } from "guid-typescript";
import axios, { AxiosHeaders, AxiosResponse, CancelTokenSource } from "axios";
import { ConsumptionDetail } from "../types/consumptionDetail";
import { formatMomentToString } from "../aplUtils/dateHelper";
import { Moment } from "moment";
import { MomentObject } from "../types/momentObject";
import { PalletStatus } from "../types/palletStatus";
import { PalletCondition } from "../types/palletCondition";

const getBlockedPallets = (pallets: PalletDetail[]) => {
    return pallets.filter(x => x.statusId === 5 || x.statusId === 10 || x.statusId === 12).sort(x => x.sscc.localeCompare(x.sscc));
};

const createPartPallet = (pallets: PalletDetail[]) =>
    aplClient().put(
        "palletdetails/partpallet",
        pallets.map((x: PalletDetail) => ({ ...x, movementViews: [], allowedPalletActions: [] }))
    );

const updatePalletsData = (pallets: PalletDetail[]) =>
    aplClient().put(
        "palletdetails",
        pallets.map(x => ({ ...x, movementViews: [], allowedPalletActions: [] }))
    );

const palletsHaveSameStatus = (pallets: PalletDetail[], statusId: number) => {
    let statuses = pallets.map(x => x.statusId);
    let arr = _.uniq(statuses);
    if (arr.length !== 1) return false;
    return arr.includes(statusId);
};

const getPalletsWithDefects = (pallets: PalletDetail[] | ConsumptionDetail[]): Promise<AxiosResponse<PalletDetail[], any>> => {
    return aplClient().post(
        "palletdetails/bypalletidmultiple",
        pallets.map(x => x.palletId.toString())
    );
};

const getPalletsWithDefectsByPalletIds = (pallets: Guid[]): Promise<AxiosResponse<PalletDetail[], any>> => {
    return aplClient().post(
        "palletdetails/bypalletidmultiple",
        pallets.map(x => x.toString())
    );
};

const getSsccByOffset = (siteId: number, sscc: string, offset: number) =>
    aplClient().get(`palletdetails/ssccbyoffset/${siteId}/${sscc}/${offset}`);

const sanitizeBarcode = (barcode: string) =>
    barcode
        ? barcode
              .trim()
              .replace(/[^a-z0-9]/gim, " ")
              .replace(/\s+/g, "X")
        : "";

const getPalletDetailsByBarcode = async (siteId: number, barcode: string) =>
    aplClient().get(`palletdetails/bysscc/${siteId}/${sanitizeBarcode(barcode)}`) as Promise<AxiosResponse<PalletDetail[], AxiosHeaders>>;

const getChildPallets = async (palletId: Guid) => aplClient().get(`childpalletdetails?palletId=${palletId}`);

let getPalletsCancelToken: CancelTokenSource | null = null;
const getPalletsByDepartmentDate = async (
    departmentId: number,
    start: Moment | MomentObject,
    end: Moment | MomentObject,
    allPallets: boolean
) => {
    getPalletsCancelToken?.cancel();
    getPalletsCancelToken = axios.CancelToken.source();

    var strStart = formatMomentToString(start);
    var strEnd = formatMomentToString(end);
    let dataUrl = `PalletDetails/bydepartmentdate/${departmentId}/${allPallets}/${strStart}/${strEnd}`;
    return aplClient().get(dataUrl, {
        cancelToken: getPalletsCancelToken?.token
    });
};

const getPalletsByDepartmentUpdate = async (
    departmentId: number,
    start: Moment | MomentObject,
    end: Moment | MomentObject,
    lastUpdated: Moment,
    allPallets: boolean
) => {
    getPalletsCancelToken?.cancel();
    getPalletsCancelToken = axios.CancelToken.source();

    let detailsUpdatedDtString = formatMomentToString(lastUpdated);
    let strStart = formatMomentToString(start);
    let strEnd = formatMomentToString(end);

    let dataUrl = `PalletDetails/bydepartmentupdate/${departmentId}/${allPallets}/${strStart}/${strEnd}/${detailsUpdatedDtString}`;
    return aplClient().get(dataUrl, {
        cancelToken: getPalletsCancelToken?.token
    });
};

const getPalletsByDepartmentDateBulk = async (
    departmentId: number,
    start: Moment | MomentObject,
    end: Moment | MomentObject,
    line: string,
    workorder: string,
    batch: string,
    lot: string,
    material: string,
    rankMin: number,
    rankMax: number,
    massOperation: boolean,
    allPallets: boolean,
    palletStatus: PalletStatus,
    palletCondition: PalletCondition
) => {
    getPalletsCancelToken?.cancel();
    getPalletsCancelToken = axios.CancelToken.source();

    let strStart = formatMomentToString(start);
    let strEnd = formatMomentToString(end);
    let dataUrl = `PalletDetails/bydepartmentdatebulk`;
    let searchParams = {
        departmentId: departmentId,
        start: strStart,
        end: strEnd,
        line: line,
        workorder: workorder,
        batch: batch,
        lot: lot,
        material: material,
        rankMin: isNaN(rankMin) ? 0 : rankMin,
        rankMax: isNaN(rankMax) ? 0 : rankMax,
        massOperation: massOperation,
        allPallets: allPallets,
        palletStatus: palletStatus,
        palletCondition: palletCondition
    };
    return aplClient().get(dataUrl, {
        params: searchParams,
        cancelToken: getPalletsCancelToken?.token
    });
};

const getPalletsByDepartmentUpdateBulk = async (
    departmentId: number,
    start: Moment | MomentObject,
    end: Moment | MomentObject,
    line: string,
    workorder: string,
    batch: string,
    lot: string,
    material: string,
    rankMin: number,
    rankMax: number,
    massOperation: boolean,
    lastUpdated: Moment | MomentObject,
    allPallets: boolean,
    palletStatus: PalletStatus,
    palletCondition: PalletCondition
) => {
    getPalletsCancelToken?.cancel();
    getPalletsCancelToken = axios.CancelToken.source();

    let strStart = formatMomentToString(start);
    let strEnd = formatMomentToString(end);
    let detailsUpdatedDtString = formatMomentToString(lastUpdated);

    let searchParams = {
        departmentId: departmentId,
        start: strStart,
        end: strEnd,
        line: line,
        workorder: workorder,
        batch: batch,
        lot: lot,
        material: material,
        rankMin: isNaN(rankMin) ? 0 : rankMin,
        rankMax: isNaN(rankMax) ? 0 : rankMax,
        massOperation: massOperation,
        lastUpdated: detailsUpdatedDtString,
        allPallets: allPallets,
        palletStatus: palletStatus,
        palletCondition: palletCondition
    };

    let dataUrl = `PalletDetails/bydepartmentupdatebulk`;
    return aplClient().get(dataUrl, {
        params: searchParams,
        cancelToken: getPalletsCancelToken?.token
    });
};

export {
    getBlockedPallets,
    createPartPallet,
    updatePalletsData,
    palletsHaveSameStatus,
    getPalletsWithDefects,
    getPalletsWithDefectsByPalletIds,
    getPalletDetailsByBarcode,
    getChildPallets,
    getSsccByOffset,
    sanitizeBarcode,
    getPalletsByDepartmentDate,
    getPalletsByDepartmentUpdate,
    getPalletsByDepartmentDateBulk,
    getPalletsByDepartmentUpdateBulk
};
